// @file Store for wall Timeline V1 format
import { convertRGBToHSL } from '@@/bits/color'
import device from '@@/bits/device'
import { useSurfaceStore } from '@@/pinia/surface'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export type DashedLineMarginPercentFromViewportBottom = 0.25 | 0.36

export const useSurfaceTimelineFormatStore = defineStore('surfaceTimelineFormatStore', () => {
  const surfaceStore = useSurfaceStore()

  // #region - Margin bottom of dashed line
  const dashedLineMarginPercentFromViewportBottom = ref<DashedLineMarginPercentFromViewportBottom>(0.36)

  const dashedLineMarginBottomClasses = computed(() => {
    if (device.app) return 'mb-36 @tablet-portrait/surface-container:mb-54'

    return dashedLineMarginPercentFromViewportBottom.value === 0.25
      ? 'mb-36 @tablet-portrait/surface-container:mb-[calc(100vh*0.25)]'
      : 'mb-36 @tablet-portrait/surface-container:mb-[calc(100vh*0.36)]'
  })

  const setDashedLineMarginPercentFromViewportBottom = (percent: DashedLineMarginPercentFromViewportBottom): void => {
    dashedLineMarginPercentFromViewportBottom.value = percent
  }
  // #endregion

  // #region - Colors
  const adjustedPrimaryBackgroundColorForDashedLine = computed(() => {
    const primaryVariantColorAsHSL = convertRGBToHSL(surfaceStore.skinPrimaryVariantColorAsRGB)

    const adjustedHSL = {
      h: primaryVariantColorAsHSL.h,
      s: surfaceStore.backgroundLuminance === 'dark' ? 50 : 35,
      l: surfaceStore.backgroundLuminance === 'dark' ? 85 : 40,
    }

    return `hsl(${adjustedHSL.h}, ${adjustedHSL.s}%, ${adjustedHSL.l}%)`
  })

  const adjustedPrimaryBackgroundColorForSectionTitle = computed(() => {
    const primaryVariantColorAsHSL = convertRGBToHSL(surfaceStore.skinPrimaryVariantColorAsRGB)

    const adjustedHSL = {
      h: primaryVariantColorAsHSL.h,
      s: surfaceStore.backgroundLuminance === 'dark' ? 50 : 35,
      l: surfaceStore.backgroundLuminance === 'dark' ? 85 : 40,
    }

    return `hsla(${adjustedHSL.h}, ${adjustedHSL.s}%, ${adjustedHSL.l}%, 0.7)`
  })

  const adjustedPrimaryBackgroundColorForCircleAndPlusButton = computed(() => {
    if (surfaceStore.backgroundLuminance === 'dark') return '#ffffff'

    const primaryVariantColorAsHSL = convertRGBToHSL(surfaceStore.skinPrimaryVariantColorAsRGB)

    const adjustedHSL = {
      h: primaryVariantColorAsHSL.h,
      s: 35,
      l: 25,
    }

    return `hsl(${adjustedHSL.h}, ${adjustedHSL.s}%, ${adjustedHSL.l}%)`
  })
  // #endregion

  return {
    // Margin bottom of dashed line
    dashedLineMarginPercentFromViewportBottom,
    dashedLineMarginBottomClasses,
    setDashedLineMarginPercentFromViewportBottom,

    // Colors
    adjustedPrimaryBackgroundColorForDashedLine,
    adjustedPrimaryBackgroundColorForSectionTitle,
    adjustedPrimaryBackgroundColorForCircleAndPlusButton,
  }
})
